import React from 'react';
import { motion } from 'framer-motion';
import { FaBaby, FaChild, FaClock, FaPalette } from 'react-icons/fa';
import { useSchool } from '../contexts/SchoolContext';
import './Programs.css';

const Programs = () => {
  const { secondaryRange } = useSchool();
  return (
    <div className="programs">
      <section className="programs-hero page-hero">
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Our Programs
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Purposeful, age-appropriate programs that cultivate curiosity and competence
          </motion.p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="program-detail">
            <div className="program-header">
              <div className="program-icon-large">
                <FaBaby />
              </div>
              <div>
                <h2>Montessori Program (Ages 3-6)</h2>
                <p className="program-age">Preschool & Kindergarten</p>
              </div>
            </div>
            <div className="program-description">
              <p>
                Our Montessori program provides a carefully prepared environment where 
                children develop independence, confidence, and a love for learning. 
                Following the Montessori philosophy, we use specialized materials and 
                child-centered approaches to support each student's natural development.
              </p>
              <h3>Key Features:</h3>
              <ul>
                <li>Child-centered, self-paced learning</li>
                <li>Hands-on Montessori materials</li>
                <li>Mixed-age classrooms promoting peer learning</li>
                <li>Focus on practical life skills</li>
                <li>Language, math, science, and cultural studies</li>
                <li>Social and emotional development</li>
                <li>Outdoor play and nature exploration</li>
              </ul>
              <h3>Daily Schedule:</h3>
              <p>8:00 AM - 3:00 PM (Extended care available)</p>
            </div>
          </div>

          <div className="program-detail">
            <div className="program-header">
              <div className="program-icon-large">
                <FaChild />
              </div>
              <div>
                <h2>Secondary Program{secondaryRange ? ` (${secondaryRange})` : ''}</h2>
                <p className="program-age">Ages 6-15</p>
              </div>
            </div>
            <div className="program-description">
              <p>
                Our Secondary program builds on the Montessori foundation while 
                introducing more structured academic subjects. We maintain a balance 
                between guided instruction and independent learning, fostering critical 
                thinking and creativity.
              </p>
              <h3>Key Features:</h3>
              <ul>
                <li>Comprehensive core curriculum (Math, Language Arts, Science, Social Studies)</li>
                <li>STEM education and technology integration</li>
                <li>Art, Music, and Physical Education</li>
                <li>Project-based learning experiences</li>
                <li>Character education and leadership development</li>
                <li>Small class sizes for individualized attention</li>
                <li>Field trips and experiential learning</li>
              </ul>
              <h3>Daily Schedule:</h3>
              <p>8:00 AM - 3:30 PM (Extended care available)</p>
            </div>
          </div>

          <div className="program-detail">
            <div className="program-header">
              <div className="program-icon-large">
                <FaClock />
              </div>
              <div>
                <h2>Extended Care Program</h2>
                <p className="program-age">All Ages</p>
              </div>
            </div>
            <div className="program-description">
              <p>
                Our extended care program provides a safe, engaging environment for 
                students before and after regular school hours. We offer a variety of 
                activities to keep children active, engaged, and learning.
              </p>
              <h3>Features:</h3>
              <ul>
                <li>Before school care: 7:00 AM - 8:00 AM</li>
                <li>After school care: 3:30 PM - 6:00 PM</li>
                <li>Homework assistance and quiet study time</li>
                <li>Enrichment activities and games</li>
                <li>Healthy snacks provided</li>
                <li>Flexible scheduling options</li>
              </ul>
            </div>
          </div>

          <div className="program-detail">
            <div className="program-header">
              <div className="program-icon-large">
                <FaPalette />
              </div>
              <div>
                <h2>Enrichment Programs</h2>
                <p className="program-age">Optional Add-ons</p>
              </div>
            </div>
            <div className="program-description">
              <p>
                We offer various enrichment programs to enhance your child's educational 
                experience and develop their talents and interests.
              </p>
              <h3>Available Programs:</h3>
              <ul>
                <li>Art classes and creative workshops</li>
                <li>Music lessons (piano, guitar, violin)</li>
                <li>Foreign language classes (Spanish, French)</li>
                <li>STEM clubs and robotics</li>
                <li>Sports and physical activities</li>
                <li>Drama and performing arts</li>
                <li>Chess club and critical thinking</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section calendar-section">
        <div className="container">
          <h2 className="section-title">Academic Calendar</h2>
          <div className="calendar-info">
            <div className="calendar-card">
              <h3>Fall Semester</h3>
              <p>September - December</p>
            </div>
            <div className="calendar-card">
              <h3>Spring Semester</h3>
              <p>January - May</p>
            </div>
            <div className="calendar-card">
              <h3>Summer Program</h3>
              <p>June - August (Optional)</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
